/**
 * =============================================================================
 * Componente: QrCodeDialog
 * Versão: 1.1.0 — Refresh visual premium (header com chip, moldura do QR,
 *                 campo URL refinado, botões Compartilhar + Baixar PNG).
 *
 * Descrição:
 *   Modal que exibe o QR Code de um link de afiliado, gerado pela Edge Function
 *   `affiliate-generate-qr`. Permite baixar o PNG, copiar a URL pública e
 *   compartilhar via Web Share API (com fallback para copiar).
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { Download, Copy, Check, Loader2, QrCode, Share2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAffiliateLinkActions } from '@/hooks/useAffiliateLinkActions';

interface QrCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linkId: string;
  /** Nome opcional para o arquivo baixado (ex.: campanha ou slug). */
  downloadFileName?: string;
}

export function QrCodeDialog({
  open,
  onOpenChange,
  linkId,
  downloadFileName,
}: QrCodeDialogProps) {
  const { generateQr, isGeneratingQr } = useAffiliateLinkActions();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [targetUrl, setTargetUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);

  // Carrega o QR sempre que o modal abre para um link.
  useEffect(() => {
    if (!open || !linkId) return;
    setDataUrl(null);
    setTargetUrl('');
    generateQr({ linkId, format: 'png', size: 512 })
      .then((res) => {
        setDataUrl(res.data_url ?? null);
        setTargetUrl(res.target_url);
      })
      .catch(() => {
        toast.error('Não foi possível gerar o QR Code');
        onOpenChange(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkId]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qrcode-${downloadFileName ?? linkId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyUrl = async () => {
    if (!targetUrl) return;
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  // Compartilhar via Web Share API quando disponível (mobile principalmente);
  // caso contrário, faz fallback para copiar a URL.
  const handleShare = async () => {
    if (!targetUrl) return;
    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: 'Meu link de indicação',
          text: 'Confere meu link de indicação:',
          url: targetUrl,
        });
        return;
      } catch {
        // usuário cancelou ou erro silencioso → cai pro fallback
      }
    }
    handleCopyUrl();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Container do modal:
        - Responsivo seguro (não rompe a viewport em mobile).
        - rounded-2xl + p-6 dão respiro premium.
        - gap-5 entre header e conteúdo.
      */}
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-full sm:max-w-md overflow-hidden rounded-2xl p-6 gap-5">
        <DialogHeader className="space-y-2">
          {/* Header com ícone-chip */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20">
              <QrCode className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold tracking-tight">
                QR Code do link
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-relaxed text-muted-foreground">
                Escaneie ou baixe a imagem para divulgar seu link.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* min-w-0 + w-full permitem que filhos flex possam encolher (truncate funcional) */}
        <div className="flex w-full min-w-0 flex-col items-center gap-5">
          {/* Moldura premium em duas camadas para o QR */}
          <div className="rounded-2xl bg-gradient-to-b from-muted/50 to-background p-1.5 ring-1 ring-border/60 shadow-sm">
            <div className="rounded-xl bg-white p-4 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
              {isGeneratingQr || !dataUrl ? (
                <Skeleton className="h-56 w-56 sm:h-60 sm:w-60 rounded-md" />
              ) : (
                <img
                  src={dataUrl}
                  alt="QR Code do link de indicação"
                  className="h-56 w-56 sm:h-60 sm:w-60 rounded-md"
                />
              )}
            </div>
          </div>

          {/* Campo URL refinado com label sutil */}
          {targetUrl && (
            <div className="w-full min-w-0 space-y-1.5">
              <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                Link da campanha
              </label>
              <div className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-border/70 bg-muted/30 px-3 py-2.5 transition-colors hover:bg-muted/50">
                <code
                  className="min-w-0 flex-1 truncate text-xs font-mono text-foreground"
                  title={targetUrl}
                >
                  {targetUrl}
                </code>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={handleCopyUrl}
                  aria-label="Copiar link"
                  className="shrink-0 transition-colors"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Footer: Compartilhar (secundário) + Baixar PNG (primário) */}
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              className="h-11 w-full gap-2 rounded-xl font-medium"
              onClick={handleShare}
              disabled={!targetUrl || isGeneratingQr}
              aria-label="Compartilhar link"
            >
              <Share2 className="h-4 w-4" />
              Compartilhar
            </Button>
            <Button
              className="h-11 w-full gap-2 rounded-xl font-semibold shadow-sm"
              onClick={handleDownload}
              disabled={!dataUrl || isGeneratingQr}
            >
              {isGeneratingQr ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar PNG
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
